import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import type { ReactNode } from 'react'
import { ConfirmationModalIcon } from './ConfirmationModalIcon'
import { ConfirmationModalLayout, ConfirmationModalShell } from './ConfirmationModalShell'
import {
  confirmationBodySx,
  confirmationHeadlineSx,
  confirmationPrimaryFullWidthButtonSx,
} from './confirmationModalStyles'

type Props = Readonly<{
  open: boolean
  headline: string
  bodyLines?: readonly [string] | readonly [string, string]
  body?: ReactNode
  primaryLabel: string
  onPrimary: () => void
  onClose?: () => void
  dialogTestId?: string
  primaryTestId?: string
}>

/** Figma 4418:10043 — alert / expired / error (single primary action). */
export function AlertStatusModal({
  open,
  headline,
  bodyLines,
  body,
  primaryLabel,
  onPrimary,
  onClose,
  dialogTestId = 'alert-status-dialog',
  primaryTestId = 'alert-status-primary',
}: Props) {
  const headlineId = `${dialogTestId}-headline`
  const bodyId = `${dialogTestId}-body`

  return (
    <ConfirmationModalShell
      open={open}
      onClose={onClose ?? onPrimary}
      dialogTestId={dialogTestId}
      ariaLabelledBy={headlineId}
      ariaDescribedBy={bodyId}
    >
      <ConfirmationModalLayout>
        <ConfirmationModalIcon variant="alert" />

        <Box
          id={bodyId}
          sx={{
            width: '100%',
            px: '24px',
            pt: '12px',
            pb: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '12px',
            textAlign: 'center',
          }}
        >
          <Typography id={headlineId} component="h2" sx={confirmationHeadlineSx}>
            {headline}
          </Typography>
          {body ??
            bodyLines?.map((line) => (
              <Typography key={line} sx={confirmationBodySx}>
                {line}
              </Typography>
            ))}
        </Box>

        <Box sx={{ width: '100%', px: '24px', py: 3 }}>
          <Button
            variant="contained"
            fullWidth
            onClick={onPrimary}
            data-testid={primaryTestId}
            sx={confirmationPrimaryFullWidthButtonSx}
          >
            {primaryLabel}
          </Button>
        </Box>
      </ConfirmationModalLayout>
    </ConfirmationModalShell>
  )
}
